import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from "react";
import { Alert, Button, ConfigProvider } from "antd";
import { analyzeThemeColor, resolveSecondaryColor } from "@tripbox/ui";
import { useCrmContext, type CrmContext } from "@/actions/crmWhiteLabel.actions";

const Context = createContext<CrmContext>({ tenantId: null, branding: null });

/** Reads the server-resolved CRM identity. */
export const useCrmBranding = () => useContext(Context);

function Metadata({ context }: { context: CrmContext }) {
  useLayoutEffect(() => {
    const brand = context.branding;
    const name = brand?.name || "Tripbox CRM";
    document.title = name;
    const icon = brand ? brand.favicon || brand.logo || "/api/crm/icon" : "/logo192.png";
    for (const key of ["application-name", "apple-mobile-web-app-title", "og:title", "og:site_name", "twitter:title"]) {
      document.querySelector(`meta[name="${key}"]`)?.setAttribute("content", name);
    }
    for (const key of ["og:image", "twitter:image", "msapplication-TileImage"]) {
      document.querySelector(`meta[name="${key}"]`)?.setAttribute("content", icon);
    }
    document.querySelector('meta[name="og:url"]')?.setAttribute("content", window.location.origin);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", brand?.secondaryColor || "#131b2a");
    for (const link of document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')) link.setAttribute("href", icon);
    if (brand) {
      const primary = analyzeThemeColor(brand.primaryColor);
      const secondary = resolveSecondaryColor(brand.secondaryColor);
      const style = document.documentElement.style;
      style.setProperty("--brand-primary", primary.color || "#333333");
      style.setProperty("--brand-primary-foreground", primary.preferredForeground);
      style.setProperty("--brand-secondary", secondary.color);
      style.setProperty("--brand-secondary-hover", secondary.hover);
      document.documentElement.dataset.whiteLabel = "true";
    } else {
      for (const key of ["--brand-primary", "--brand-primary-foreground", "--brand-secondary", "--brand-secondary-hover"]) document.documentElement.style.removeProperty(key);
      delete document.documentElement.dataset.whiteLabel;
    }
  }, [context]);
  return null;
}

/** Loads domain context before any routes, logos, or marketing pages can render. */
export function CrmBrandingProvider({ children }: { children: ReactNode }) {
  const query = useCrmContext();
  if (query.isPending) return <div className="p-12 text-center text-gray-700" role="status">Loading workspace…</div>;
  if (query.isError) return <div className="mx-auto max-w-xl p-6 pt-20"><Alert type="error" showIcon
    message="Workspace unavailable" description={query.error.message}
    action={<Button onClick={() => query.refetch()}>Retry</Button>} /></div>;
  const context = query.data;
  const branding = context.branding;
  const secondary = branding ? resolveSecondaryColor(branding.secondaryColor).color : undefined;
  return <Context.Provider value={context}>
    <Metadata context={context} />
    <ConfigProvider theme={branding ? { token: { colorPrimary: secondary, colorInfo: branding.primaryColor, colorLink: branding.primaryColor } } : {}}>
      {children}
    </ConfigProvider>
  </Context.Provider>;
}

/** Displays the tenant logo with a tenant-name fallback, never a platform logo on custom domains. */
export function CrmBrand({ className = "h-8 w-auto", platformLogo = "/logo_text_black.png" }: { className?: string; platformLogo?: string }) {
  const { branding } = useCrmBranding();
  const [failed, setFailed] = useState<string | null>(null);
  const src = branding ? branding.logo : platformLogo;
  return src && failed !== src ? <img src={src} alt={branding?.name || "Tripbox CRM"} className={className}
    onError={() => setFailed(src)} /> : <span className="text-lg font-semibold break-words">{branding?.name || "Workspace"}</span>;
}
